/**
 * US state abbreviations → full names (for display and normalizing CMS data).
 */
export const STATE_ABBR_TO_NAME: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const NAME_TO_ABBR: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_ABBR_TO_NAME).map(([abbr, name]) => [name.toLowerCase(), abbr])
);

// DC is handled separately: the main table intentionally omits it for the
// Sanity "states" dropdown, but the reverse-geocode path should still map it.
NAME_TO_ABBR['district of columbia'] = 'DC';

function speciesStatesMatchHomeState(
  states: string[],
  homeState: { name: string; abbreviation: string }
): boolean {
  const homeAbbr = homeState.abbreviation.trim().toUpperCase();
  const homeNameLower = homeState.name.trim().toLowerCase();

  return states.some((entry) => {
    const raw = String(entry ?? '').trim();
    if (!raw) return false;

    const upper = raw.toUpperCase();

    if (upper === homeAbbr) return true;

    if (raw.toLowerCase() === homeNameLower) return true;

    const fullFromAbbr = STATE_ABBR_TO_NAME[upper];
    if (fullFromAbbr && fullFromAbbr.toLowerCase() === homeNameLower) return true;

    const abbrFromName = NAME_TO_ABBR[raw.toLowerCase()];
    if (abbrFromName === homeAbbr) return true;

    return false;
  });
}

/**
 * Whether to show "Found" / "Not found" badges for map UI.
 * - `null`: no home state, or no usable states list → show neither badge
 * - `found` / `not-found`: home set and we have a non-empty states array
 */
export function getHomeStateRangeBadgeKind(
  states: string[] | undefined | null,
  homeState: { name: string; abbreviation: string } | null | undefined
): 'found' | 'not-found' | null {
  if (!homeState?.abbreviation?.trim()) return null;
  if (!Array.isArray(states)) return null;
  if (states.length === 0) return 'not-found';

  return speciesStatesMatchHomeState(states, homeState) ? 'found' : 'not-found';
}


/**
 * Convert a full US state name (e.g. "Montana") to its USPS code ("MT").
 * Accepts an already-2-letter USPS code case-insensitively as a passthrough.
 * Returns null for unmatched input.
 */
export function stateNameToUsps(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const fromName = NAME_TO_ABBR[lower];
  if (fromName) return fromName;
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && upper in STATE_ABBR_TO_NAME) return upper;
  if (upper === 'DC') return 'DC';
  return null;
}

/**
 * Extract a USPS state code from a reverseGeocode() string, which is typically
 * "County, State Name" (e.g. "Gallatin County, Montana"). Returns null if no
 * state can be resolved.
 */
export function stateFromReverseGeocode(
  label: string | null | undefined
): string | null {
  if (!label) return null;
  const parts = label.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return stateNameToUsps(parts[parts.length - 1]);
}
